import React, { useState, useEffect } from 'react';
import { Checkbox, DatePicker, Switch, Select, Button } from 'antd';
import moment from 'moment';
import 'moment-timezone'; // Import for timezone support if needed
import { useHistoryData } from '@/components/Kanban/Workbook/DataHooks';
import { CloseOutlined } from '@ant-design/icons'; // Import the icon

const { Option } = Select;

const HistoryGroupContent = ({
  isGroupingEnabled,
  onGroupingEnabledChange,
  selectedGroup,
  setSelectedGroup,
  selectedRange,
  setSelectedRange,
}) => {
  const historyData = useHistoryData();
  const [creationEvents, setCreationEvents] = useState([]);

  useEffect(() => {
    if (historyData && Array.isArray(historyData)) {
      const creations = historyData.filter(
        (entry) =>
          entry.event &&
          entry.event.startsWith('New Initiative') &&
          entry.event.endsWith('Created') &&
          entry.created
      );
      setCreationEvents(creations);
    }
  }, [historyData]);

  const handleCheckboxChange = (checkedValues) => {
    setSelectedGroup(checkedValues);
    setSelectedRange([]);
  };

  const handleDateChangeFrom = (date) => {
    setSelectedRange([
      date ? moment(date).toISOString() : undefined,
      selectedRange ? selectedRange[1] : undefined,
    ]);
    setSelectedGroup([]); // Clear checkboxes on manual date selection
  };

  const handleDateChangeTo = (date) => {
    setSelectedRange([
      selectedRange ? selectedRange[0] : undefined,
      date ? moment(date).toISOString() : undefined,
    ]);
    setSelectedGroup([]); // Clear checkboxes on manual date selection
  };

  const handleTimePeriodSelect = (value) => {
    setSelectedRange([]);
    setSelectedGroup([]);
    const now = moment();
    let startDate, endDate;

    switch (value) {
      case 'last7days':
        startDate = now.clone().subtract(7, 'day').startOf('day');
        endDate = now.clone().endOf('day');
        break;
      case 'last14days':
        startDate = now.clone().subtract(14, 'day').startOf('day');
        endDate = now.clone().endOf('day');
        break;
      case 'last30days':
        startDate = now.clone().subtract(30, 'day').startOf('day');
        endDate = now.clone().endOf('day');
        break;
      case 'last60days':
        startDate = now.clone().subtract(60, 'day').startOf('day');
        endDate = now.clone().endOf('day');
        break;
      default:
        return;
    }

    if (value !== 'manual') {
      // Filter creation events within this range based on 'created'
      const filtered = creationEvents.filter(
        (event) =>
          event.created &&
          moment(event.created).isAfter(startDate) &&
          moment(event.created).isBefore(endDate)
      );
      setSelectedGroup(filtered.map((event) => event.created));
    }
  };

  const handleClearSelection = () => {
    setSelectedGroup([]);
    setSelectedRange([]);
  };

  return (
    <div style={{ padding: '10px', maxHeight: '500px', overflowY: 'auto' }}>
      <div
        style={{
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ color: 'black' }}>Enable grouping</span>
        <Switch checked={isGroupingEnabled} onChange={onGroupingEnabledChange} />
        {(selectedGroup && selectedGroup.length > 0) ||
          (selectedRange && selectedRange.length > 0) ? (
          <Button onClick={handleClearSelection} icon={<CloseOutlined />} style={{ color: 'blue' }}>
            Clear
          </Button>
        ) : null}
      </div>

      <>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
            Select a creation time
          </label>
          <Select
            onChange={handleTimePeriodSelect}
            style={{ width: '100%' }}
            placeholder="Select predefined period"
            defaultValue="manual" // Set default to manual to match image
          >
            <Option value="manual">Manual selection</Option>
            <Option value="last7days">Last 7 days</Option>
            <Option value="last14days">Last 14 days</Option>
            <Option value="last30days">Last 30 days</Option>
            <Option value="last60days">Last 60 days</Option>
          </Select>
        </div>

        <div style={{ marginBottom: '8px' }}>
          <label style={{ display: 'block', marginBottom: '4px' }}>Created From</label>
          <DatePicker
            onChange={(date) => handleDateChangeFrom(date ? moment(date) : null)}
            format="MMM D, hh:mm A"
            showTime
            value={selectedRange && selectedRange[0] ? moment(selectedRange[0]) : null}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '4px' }}>Created To</label>
          <DatePicker
            onChange={(date) => handleDateChangeTo(date ? moment(date) : null)}
            format="MMM D, hh:mm A"
            showTime
            value={selectedRange && selectedRange[1] ? moment(selectedRange[1]) : null}
            style={{ width: '100%' }}
          />
        </div>

        <Checkbox.Group
          value={selectedGroup}
          onChange={handleCheckboxChange}
          style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: '16px' }}
        >
          {creationEvents.map((event) => (
            <Checkbox key={event.created} value={event.created}>
              {moment(event.created).format('MMM D, hh:mm A')}
            </Checkbox>
          ))}
        </Checkbox.Group>
      </>
    </div>
  );
};

export default HistoryGroupContent;
